import * as _ from 'lodash';
import { EventRegistry } from '../events/event_registry';
import DebugService from '../debug.service';

const log = (...args) => DebugService.log('ClockHandlerFSM', ...args);
/**
 * This StateMachine, aka finite state machine, is to help the clock handler manage many events and
 * trigger actions to be executed by the clock handler.
 *
 * With this approach it is very easy to access what state the clock handler is in. It's also
 * very easy to declare what actions should be taken for each state and for each event.
 *
 * We are also able to separate actions from what actually occurs during the action which can
 * come in handy when fine tuning the behaviour of the clock handler.
 *
 * See the BaseClockHandlerFSMState for a description of events.
 */
export class ClockHandlerFSM {
    private readonly _actionRegistry: EventRegistry;
    private _currentState: ClockHandlerFSMBaseState;
    private _started: boolean;

    static get LIVE_STATE() {
        return 'live';
    }

    static get NOT_LIVE_PLAY_STATE() {
        return 'not_live_play';
    }

    static get PAUSED_STATE() {
        return 'paused';
    }

    static get PLAY_ACTION() {
        return 'play_action';
    }

    static get PAUSE_ACTION() {
        return 'pause_action';
    }

    static get LIVE_ACTION() {
        return 'live_action';
    }

    static get DISABLE_LIVE_ACTION() {
        return 'disable_live_action';
    }

    static get SET_CLOCK_MULTIPLIER_ACTION() {
        return 'set_clock_mult_action';
    }

    constructor() {
        this._actionRegistry = new EventRegistry();
        this._currentState = new LiveState(this._actionRegistry);
        this._started = false;
    }

    /**
     * Required as the clock handler as an init function. Call this in the init function
     * to make sure we have the on entry conditions set up for the initial state
     */
    start() {
        if (this._started) {
            throw new Error('state machine is already running');
        }
        this._started = true;
        this._currentState.onEntry();
        this._actionRegistry.notifyListeners(ClockHandlerFSM.PLAY_ACTION);
    }

    onTogglePlayPause() {
        log('on toggle play pause');
        this._changeStates(this._currentState.onTogglePlayPause());
    }

    onScrubEnterLive() {
        log('on scrub enter live');
        this._changeStates(this._currentState.onScrubEnterLive());
    }

    onScrubExitLive() {
        log('on scrub exit live');
        this._changeStates(this._currentState.onScrubExitLive());
    }

    onPlayEnterLive(playSpeedMultiplier) {
        log('on play enter live');
        if (_.isNil(playSpeedMultiplier)) {
            throw new Error('playSpeedMultiplier cannot be nil');
        }
        if (playSpeedMultiplier === 0) {
            throw new Error('playSpeedMultiplier cannot be 0');
        }
        this._changeStates(this._currentState.onPlayEnterLive(playSpeedMultiplier));
    }

    onPlayExitLive() {
        log('on play exit live');
        this._changeStates(this._currentState.onPlayExitLive());
    }

    onSetPlayMultiplier(playMultiplier) {
        log('on set play multiplier', playMultiplier);
        this._changeStates(this._currentState.onSetPlayMultiplier(playMultiplier));
    }

    onToggleLive() {
        log('on toggle live');
        this._changeStates(this._currentState.onToggleLive());
    }

    onServerTimeOffsetChanged() {
        log('on server time offset changed');
        this._changeStates(this._currentState.onServerTimeOffsetChanged());
    }

    addActionListener(listener) {
        log('add action listener');
        return this._actionRegistry.addEventListener(listener);
    }

    getStateName() {
        return this._currentState.name;
    }

    _changeStates(newState) {
        if (!this._started) {
            throw new Error('Cannot change states, state machine has not started');
        }
        if (this._currentState !== newState) {
            log(`state change : ${this._currentState.name} -> ${newState.name}`);
            // Exit the old state
            this._currentState.onExit();
            // Internally apply the new state
            this._currentState = newState;
            // Enter the new state
            newState.onEntry();
        }
    }
}

class ClockHandlerFSMBaseState {
    _actionRegistry;
    _name;

    constructor(actionRegistry) {
        if (!actionRegistry) {
            throw new Error('actionRegistry cannot be undefined');
        }
        this._actionRegistry = actionRegistry;
    }

    /**
     * Used by subclasses for creating more instances of that subclass
     * @returns {*}
     */
    get actionRegistry() {
        return this._actionRegistry;
    }

    /**
     * Used by subclasses to notify listeners of an action
     * @param args
     */
    requestAction(...args) {
        this._actionRegistry.notifyListeners(...args);
    }

    /**
     * Return the name of this state. Handy for working out which state we are in
     */
    get name() {
        return this._name;
    }

    /**
     * Runs when we transition into a state. Does not run on self transitions
     */
    onEntry() {}

    /**
     * Runs when we transition out of a state. Does not run on self transitions
     */
    onExit() {}

    /**
     * User toggles play / pause
     */
    onTogglePlayPause() {
        throw new Error('not implemented');
    }

    /**
     * User toggles live
     */
    onToggleLive() {
        throw new Error('not implemented');
    }

    /**
     * User has triggered a manual move of the cursor pointer and entered the
     * live zone.
     */
    onScrubEnterLive() {
        throw new Error('not implemented');
    }

    /**
     * User has triggered a manual move of the cursor pointer and exited the
     * live zone.
     */
    onScrubExitLive() {
        throw new Error('not implemented');
    }

    /**
     * Entered live zone via normal clock playing or using the time bumpers
     * @param playSpeedMultiplier The current play speed multiplier of the cesium clock
     */
    onPlayEnterLive(playSpeedMultiplier) {
        throw new Error('not implemented');
    }

    /**
     * Exited live zone via normal clock playing or using the time bumpers
     */
    onPlayExitLive() {
        throw new Error('not implemented');
    }

    /**
     * The user has set the play multiplier
     * @param playSpeedMultiplier The new play speed multiplier
     */
    onSetPlayMultiplier(playSpeedMultiplier) {
        throw new Error('not implemented');
    }

    /**
     * The difference between the server time and the client time has changed
     */
    onServerTimeOffsetChanged() {
        throw new Error('not implemented');
    }
}

/**
 * The clock is playing, and the cursor is at a certain point in time
 */
class LiveState extends ClockHandlerFSMBaseState {
    constructor(actionRegistry) {
        super(actionRegistry);
        this._name = ClockHandlerFSM.LIVE_STATE;
    }

    onEntry() {
        this.requestAction(ClockHandlerFSM.LIVE_ACTION);
    }

    onExit() {
        this.requestAction(ClockHandlerFSM.DISABLE_LIVE_ACTION);
    }

    onScrubEnterLive() {
        // A work around because the cesium clock seems to stop when the
        // user scrubs to the far right edge. This action kicks
        // everything off again to keep the clock moving while in live mode
        this.requestAction(ClockHandlerFSM.PLAY_ACTION);
        return this; // do nothing
    }

    onScrubExitLive() {
        return new Paused(this.actionRegistry);
    }

    onSetPlayMultiplier() {
        return this; // do nothing
    }

    onTogglePlayPause() {
        return new Paused(this.actionRegistry);
    }

    onPlayEnterLive(playSpeedMultiplier) {
        if (playSpeedMultiplier > 0) {
            return this;
        } else {
            return new NotLivePlay(this.actionRegistry);
        }
    }

    onPlayExitLive() {
        return new NotLivePlay(this.actionRegistry);
    }

    onToggleLive() {
        return new Paused(this.actionRegistry);
    }

    onServerTimeOffsetChanged() {
        this.requestAction(ClockHandlerFSM.LIVE_ACTION);
        return this;
    }
}

/**
 * When the clock is playing, but not in live mode
 */
class NotLivePlay extends ClockHandlerFSMBaseState {
    constructor(actionRegistry) {
        super(actionRegistry);
        this._name = ClockHandlerFSM.NOT_LIVE_PLAY_STATE;
    }

    onSetPlayMultiplier(multiplier) {
        this.requestAction(ClockHandlerFSM.SET_CLOCK_MULTIPLIER_ACTION, multiplier);
        return this;
    }

    onScrubExitLive() {
        return new Paused(this.actionRegistry);
    }

    onScrubEnterLive() {
        return new LiveState(this.actionRegistry);
    }

    onTogglePlayPause() {
        return new Paused(this.actionRegistry);
    }

    onPlayEnterLive(playSpeedMultiplier) {
        if (playSpeedMultiplier > 0) {
            // if we're playing forward and we hit live time, switch to live mode; we've caught up.
            return new LiveState(this.actionRegistry);
        } else {
            // if we're playing in reverse, keep on playing in reverse,
            // don't change into live mode.
            return this;
        }
    }

    onPlayExitLive() {
        return this;
    }

    onToggleLive() {
        return new LiveState(this.actionRegistry);
    }

    onServerTimeOffsetChanged() {
        return this;
    }
}

/**
 * The clock is paused
 */
class Paused extends ClockHandlerFSMBaseState {
    constructor(actionRegistry) {
        super(actionRegistry);
        this._name = ClockHandlerFSM.PAUSED_STATE;
    }

    onEntry() {
        this.requestAction(ClockHandlerFSM.PAUSE_ACTION);
    }

    onExit() {
        this.requestAction(ClockHandlerFSM.PLAY_ACTION);
    }

    onScrubEnterLive() {
        return new LiveState(this.actionRegistry);
    }

    onTogglePlayPause() {
        return new NotLivePlay(this.actionRegistry);
    }

    onSetPlayMultiplier(multiplier) {
        this.requestAction(ClockHandlerFSM.SET_CLOCK_MULTIPLIER_ACTION, multiplier);
        return this;
    }

    onScrubExitLive() {
        return this;
    }

    onToggleLive() {
        return new LiveState(this.actionRegistry);
    }

    onPlayEnterLive(playSpeedMultiplier) {
        // we're paused and have been jumped to the live spot somehow.
        // This can happen when the user jumps to a particular time manually.
        // In this case we always want to go into live mode.
        // It's just more consistent.
        return new LiveState(this.actionRegistry);
    }

    onPlayExitLive() {
        return this;
    }

    onServerTimeOffsetChanged() {
        return this;
    }
}
